import React, { useMemo } from 'react';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { Text } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { useTeams } from '@atlassian/global-shortcuts/teams';
// Taken directly from the teams app definition to be used with global app shortcuts
import { useCreateTeamModal } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/ui/use-create-team-modal/index.tsx';
import { useInvitePeopleModal } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/ui/use-invite-people-modal.tsx';
import messages from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/ui/messages.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { FIXED_ITEM_DROPDOWN_ACTION } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { LEVEL_TWO } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { GLOBAL_APP_SHORTCUT_TEAMS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_TEAMS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { getUserNavigationItemLinks } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { JWM_INVITE_TEAM_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { InviteTeamNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-invite-team-tour/async.tsx';

const CREATE_A_TEAM = 'createATeam';
const INVITE_PEOPLE = 'invitePeople';

export const useTeamsAppShortcut = () => {
	const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();
	const peopleMenuWrapperRef = useRegisterNudgeTarget<HTMLAnchorElement>(
		JWM_INVITE_TEAM_NUDGE_ID,
		fg('teams-app-shortcut-nudge'),
	).ref;
	const teamsConfig = useTeams(USER_NAVIGATION_ITEM_LINKS[MENU_ID_TEAMS], {
		ref: peopleMenuWrapperRef,
	});
	const { formatMessage } = useIntl();
	const invitePeopleModalTrigger = useInvitePeopleModal();
	const [createTeamModal, createTeamModalTrigger] = useCreateTeamModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const component = useMemo(() => {
		return (
			<>
				{fg('teams-app-shortcut-nudge') && <InviteTeamNudgeAsync />}
				{createTeamModal}
			</>
		);
	}, [createTeamModal]);

	const config = useMemo(() => {
		const invitePeopleAction = fg('hot-121631-disable-teams-menu-invite-people')
			? []
			: [
					{
						onClick: (): void => {
							fireUIAnalytics(createAnalyticsEvent({}), {
								action: 'clicked',
								actionSubject: 'dropdownItem',
								actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS],
								attributes: {
									level: LEVEL_TWO,
									componentType: FIXED_ITEM_DROPDOWN_ACTION,
									parentItemId: GLOBAL_APP_SHORTCUT_TEAMS,
									itemId: INVITE_PEOPLE,
								},
							});
							invitePeopleModalTrigger();
						},
						component: <Text>{formatMessage(messages.invitePeople)}</Text>,
					},
				];

		return {
			...teamsConfig,
			actions: [
				...invitePeopleAction,
				{
					onClick: () => {
						fireUIAnalytics(createAnalyticsEvent({}), {
							action: 'clicked',
							actionSubject: 'dropdownItem',
							actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS],
							attributes: {
								level: LEVEL_TWO,
								componentType: FIXED_ITEM_DROPDOWN_ACTION,
								parentItemId: GLOBAL_APP_SHORTCUT_TEAMS,
								itemId: CREATE_A_TEAM,
							},
						});
						createTeamModalTrigger();
					},
					component: <Text>{formatMessage(messages.createTeam)}</Text>,
				},
			],
		};
	}, [
		teamsConfig,
		createTeamModalTrigger,
		invitePeopleModalTrigger,
		formatMessage,
		createAnalyticsEvent,
	]);

	return {
		config,
		component,
	};
};
